import { LuLandmark } from "react-icons/lu";
import { SQUARES, priceOf, type MonopolyView, type Sq } from "../../../shared/games/monopoly";
import { DIE_FACE, GROUP_COLOR, SEAT_COLOR, kr } from "./common";

/** Square index -> [row, col] on an 11×11 ring (START bottom-right, going clockwise: bottom row leftwards). */
function cell(i: number): [number, number] {
  if (i <= 10) return [11, 11 - i];
  if (i <= 20) return [11 - (i - 10), 1];
  if (i <= 30) return [1, 1 + (i - 20)];
  return [1 + (i - 30), 11];
}
const sqIcon = (q: Sq) => ({ go: "▶", chest: "🎁", chance: "❓", jail: "🔒", parking: "🅿️", gojail: "👮", tax: "💸", rail: "🚂", util: q.t === "util" && q.name === "Elværket" ? "💡" : "💧" } as Record<string, string>)[q.t] ?? "";

/**
 * The flat board – used when WebGL is not available (or when the player has chosen the simple view). Same information as the 3D board:
 * owners, houses, mortgages, figures, the dice; a click on a field selects it.
 */
export function Board2D({ b, you, myTurn, names, selected, onPick, pick }: { b: MonopolyView; you: number; myTurn: boolean; names: (s: number) => string; selected: number | null; onPick: (i: number) => void; pick?: { tiles: number[] | null; color: string } }) {
  const pos = b.players[you]?.pos ?? 0;
  const dice = b.dice;
  return (
    <div className="mono-board" role="group" aria-label="EduPoly-brættet">
      {SQUARES.map((q, i) => {
        const [r, c] = cell(i);
        const owner = b.owner[i];
        const corner = i % 10 === 0;
        const side = i < 10 ? "bottom" : i < 20 ? "left" : i < 30 ? "top" : "right";
        const here = b.players.map((pl, s) => ({ pl, s })).filter(({ pl }) => !pl.out && pl.pos === i);
        const h = b.houses[i]!;
        const label = `${q.name}${q.t === "prop" || q.t === "rail" || q.t === "util" ? ` – ${kr(priceOf(i))}` : ""}`;
        return (
          <button type="button" key={i} onClick={() => onPick(i)} aria-label={label} aria-pressed={selected === i}
            className={`sq ${q.t} ${corner ? "corner" : ""} ${side} ${b.mort[i] ? "mort" : ""} ${pos === i && myTurn ? "me-here" : ""} ${selected === i ? "sel" : ""} ${pick?.tiles?.includes(i) ? "pick" : ""}`} style={{ gridRow: r, gridColumn: c, ...(pick?.tiles?.includes(i) ? { "--pick": pick.color } : {}) }} title={label}>
            {q.t === "prop" && <span className="band" style={{ background: GROUP_COLOR[q.group] }}>{h > 0 && <span className="pips" aria-label={h === 5 ? "hotel" : `${h} huse`}>{h === 5 ? <b className="hotel" /> : Array.from({ length: h }, (_, k) => <b key={k} className="house" />)}</span>}</span>}
            <span className="sqname">{sqIcon(q) && <i aria-hidden="true">{sqIcon(q)}</i>}{q.name}</span>
            {(q.t === "prop" || q.t === "rail" || q.t === "util") && <span className="sqprice">{priceOf(i)}</span>}
            {q.t === "tax" && <span className="sqprice">{q.amount}</span>}
            {owner !== null && owner !== undefined && <span className="owner" style={{ background: SEAT_COLOR[owner] }} aria-label={`Ejes af ${names(owner)}`} />}
            {here.length > 0 && <span className="tokens">{here.map(({ s }) => <span key={s} className="tok" style={{ background: SEAT_COLOR[s] }} aria-label={names(s)}>{names(s)[0]!.toUpperCase()}</span>)}</span>}
          </button>
        );
      })}
      <div className="mono-center">
        <div className="mono-logo"><LuLandmark aria-hidden="true" size={22} /> EduPoly</div>
        <div className="dice" role="img" aria-label={`Terningerne viser ${dice[0]} og ${dice[1]}`}>
          <span className={b.rolled ? "roll" : ""} key={`${dice[0]}${dice[1]}${b.rolled}`}>{DIE_FACE[dice[0]]}</span><span className={b.rolled ? "roll" : ""} key={`b${dice[0]}${dice[1]}${b.rolled}`}>{DIE_FACE[dice[1]]}</span>
        </div>
        {b.lastCard && !b.q && <div className={`card-note ${b.lastCard.deck}`}><b>{b.lastCard.deck === "chance" ? "Chancen" : "Fælleskassen"}</b> {b.lastCard.text}</div>}
      </div>
    </div>
  );
}
